import React from "react";
import { Check } from "lucide-react";

// WO#8 — Curated caption-style catalog. IMMUTABLE. Each entry maps a friendly
// customer-facing `label` to the EXACT, case-sensitive Submagic `templateName`
// (never shown to customers). `variant` selects the code-native swatch artwork
// only — Submagic renders the real built-in caption style at export time; we do
// NOT recreate, customize, or CSS-simulate any caption behavior. Default visible
// selection is Bold Impact (Hormozi 2).
//
// SHARED: this is the single source of the eight caption-style definitions +
// their swatch artwork. Both the Dashboard workspace picker AND the first-run
// template wizard import from here so there is exactly ONE definition + one
// swatch renderer (no drift, no duplication).
export const CAPTION_STYLES = Object.freeze([
    Object.freeze({ id: "bold-impact", label: "Bold Impact", templateName: "Hormozi 2", variant: "bold-impact" }),
    Object.freeze({ id: "power-pop", label: "Power Pop", templateName: "Beast", variant: "power-pop" }),
    Object.freeze({ id: "clean-focus", label: "Clean Focus", templateName: "Ali", variant: "clean-focus" }),
    Object.freeze({ id: "modern-minimal", label: "Modern Minimal", templateName: "Iman", variant: "modern-minimal" }),
    Object.freeze({ id: "neon-punch", label: "Neon Punch", templateName: "Kendrick", variant: "neon-punch" }),
    Object.freeze({ id: "highlight-block", label: "Highlight Block", templateName: "Dan 2", variant: "highlight-block" }),
    Object.freeze({ id: "orange-edge", label: "Orange Edge", templateName: "Leon", variant: "orange-edge" }),
    Object.freeze({ id: "star-power", label: "Star Power", templateName: "William", variant: "star-power" }),
]);

export const DEFAULT_CAPTION_TEMPLATE = "Hormozi 2";
export const CAPTION_SWATCH_SAMPLE = "SAMPLE";

// Code-native selector artwork for one style. Uses only loaded fonts / system
// fallbacks and rich (non-raw) colors. Radius kept <= 8px; no nested cards.
export const CaptionSwatch = ({ variant }) => {
    const band =
        "flex h-14 items-center justify-center overflow-hidden rounded-lg px-3 text-center";
    switch (variant) {
        case "bold-impact":
            return (
                <div className={`${band} bg-neutral-900`}>
                    <span
                        className="text-base font-black uppercase tracking-tight text-white sm:text-lg"
                        style={{ textShadow: "0 2px 12px rgba(0,0,0,0.95)" }}
                    >
                        {CAPTION_SWATCH_SAMPLE}
                    </span>
                </div>
            );
        case "power-pop":
            return (
                <div className={`${band} bg-neutral-800`}>
                    <span
                        className="text-base font-black italic uppercase text-white sm:text-lg"
                        style={{
                            textShadow:
                                "1.5px 1.5px 0 #000, -1.5px -1.5px 0 #000, 1.5px -1.5px 0 #000, -1.5px 1.5px 0 #000",
                        }}
                    >
                        {CAPTION_SWATCH_SAMPLE}
                    </span>
                </div>
            );
        case "clean-focus":
            return (
                <div className={`${band} bg-neutral-800`}>
                    <span className="rounded-md bg-neutral-200 px-2 py-1 text-base font-semibold text-neutral-900 sm:text-lg">
                        {CAPTION_SWATCH_SAMPLE}
                    </span>
                </div>
            );
        case "modern-minimal":
            return (
                <div className={`${band} bg-neutral-900`}>
                    <span className="text-sm font-light uppercase tracking-[0.2em] text-neutral-100 sm:text-base">
                        {CAPTION_SWATCH_SAMPLE}
                    </span>
                </div>
            );
        case "neon-punch":
            return (
                <div className={`${band} bg-neutral-900`}>
                    <span
                        className="rounded-md px-2 py-1 text-base font-extrabold uppercase text-black sm:text-lg"
                        style={{ backgroundColor: "#22c55e" }}
                    >
                        {CAPTION_SWATCH_SAMPLE}
                    </span>
                </div>
            );
        case "highlight-block":
            return (
                <div className={`${band} bg-neutral-900`}>
                    <span
                        className="rounded-md px-2 py-1 text-base font-extrabold uppercase text-black sm:text-lg"
                        style={{ backgroundColor: "#facc15" }}
                    >
                        {CAPTION_SWATCH_SAMPLE}
                    </span>
                </div>
            );
        case "orange-edge":
            return (
                <div className={`${band} bg-neutral-900`}>
                    <span
                        className="text-base font-black uppercase text-white sm:text-lg"
                        style={{
                            textShadow:
                                "1.5px 1.5px 0 #ea580c, -1.5px -1.5px 0 #ea580c, 1.5px -1.5px 0 #ea580c, -1.5px 1.5px 0 #ea580c",
                        }}
                    >
                        {CAPTION_SWATCH_SAMPLE}
                    </span>
                </div>
            );
        case "star-power":
            return (
                <div className={`${band} bg-neutral-900`}>
                    <span
                        className="rounded-md px-2 py-1 text-base font-black uppercase text-black sm:text-lg"
                        style={{ backgroundColor: "#eab308" }}
                    >
                        {CAPTION_SWATCH_SAMPLE}
                    </span>
                </div>
            );
        case "custom":
            // FULCRUM custom theme tile — on-brand purple accent (solid), radius
            // <= 8px. Purely presentational; the real caption look is whatever
            // the operator's Submagic custom theme renders at export time.
            return (
                <div className={`${band} bg-neutral-900`}>
                    <span
                        className="rounded-md px-2 py-1 text-base font-black uppercase text-white sm:text-lg"
                        style={{ backgroundColor: "#A855F7" }}
                    >
                        {CAPTION_SWATCH_SAMPLE}
                    </span>
                </div>
            );
        default:
            return <div className={`${band} bg-neutral-900`} />;
    }
};

/**
 * Presentational, controlled grid of selectable caption-style tiles — each tile
 * renders the SHARED visual sample (CaptionSwatch) plus the friendly label, and
 * marks the selected tile with a check + aria-checked. Emits the exact Submagic
 * `templateName` (never a label or id) through `onSelect`. Reused by the
 * first-run template wizard so it shows the same eight visual samples as the
 * Dashboard workspace picker.
 */
export const CaptionStyleGrid = ({
    styles = [],
    value,
    onSelect,
    disabled = false,
    testIdPrefix = "caption-style",
    ariaLabel = "Caption style",
}) => (
    <div
        role="radiogroup"
        aria-label={ariaLabel}
        data-testid={`${testIdPrefix}-grid`}
        className="grid grid-cols-2 gap-3 sm:grid-cols-4"
    >
        {styles.map((s) => {
            const selected = s.templateName === value;
            return (
                <button
                    key={s.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-label={s.label}
                    tabIndex={selected ? 0 : -1}
                    data-testid={`${testIdPrefix}-${s.id}`}
                    disabled={disabled}
                    onClick={() => onSelect?.(s.templateName)}
                    className={[
                        "group flex flex-col rounded-lg border p-2 text-left",
                        "transition-[border-color,box-shadow] duration-200",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70",
                        "disabled:cursor-not-allowed disabled:opacity-50",
                        selected
                            ? "border-purple-400/80 shadow-[0_0_24px_-8px_rgba(168,85,247,0.75)]"
                            : "border-white/12 hover:border-purple-400/40",
                    ].join(" ")}
                >
                    <CaptionSwatch variant={s.variant} />
                    <div className="mt-2 flex items-center justify-between gap-2 px-0.5">
                        <span className="truncate text-sm font-semibold text-white/85">
                            {s.label}
                        </span>
                        {selected && (
                            <Check
                                data-testid={`${testIdPrefix}-check-${s.id}`}
                                className="h-4 w-4 flex-none text-purple-300"
                            />
                        )}
                    </div>
                </button>
            );
        })}
    </div>
);

export default CaptionStyleGrid;
